/**
 * Avatar.
 *
 * Initials on a coloured disc, not an image. `users.avatar_url` exists in the
 * schema and nothing populates it: the column is there so that adding avatars
 * later is a write rather than a migration, and rendering a broken image for
 * every user in the meantime would be worse than rendering their initials.
 *
 * The colour comes from the user record rather than being derived from a hash of
 * the id. That is a deliberate choice made on the server - see the comment on
 * `User.colour` in `packages/shared/src/domain.ts` - and it is what makes two
 * people in the same workspace reliably distinguishable instead of occasionally
 * identical.
 *
 * The text colour is computed from the background with a WCAG luminance
 * calculation rather than being hard-coded white. A user picks their own colour,
 * and white text on a pale yellow is unreadable.
 */

import type { ReactNode } from 'react';
import { initials, readableInkOn } from '../lib/format.js';

export interface AvatarProps {
  name: string;
  colour: string;
  /** Diameter in pixels. The three sizes in use are 20 (inline), 28 (presence
   * stack) and 40 (drawer header). */
  size?: number;
  /** Renders a ring, used to mark the current user in the presence stack. */
  ringed?: boolean;
  title?: string;
}

export function Avatar({ name, colour, size = 28, ringed = false, title }: AvatarProps): ReactNode {
  return (
    <span
      // `title` on a non-interactive element is a tooltip, which is the right
      // affordance for "who is this" but is not a substitute for a label - the
      // presence stack has its own accessible text.
      title={title ?? name}
      aria-hidden={title === undefined ? undefined : true}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold select-none',
        ringed ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        width: size,
        height: size,
        backgroundColor: colour,
        color: readableInkOn(colour),
        fontSize: Math.round(size * 0.42),
      }}
    >
      {initials(name)}
    </span>
  );
}
